import * as THREE from 'three';
import { DirtyRange, FOG_FRAG, FOG_VERT, fxUniforms } from './common';

/** A faceted crystal of unit width and height: a slightly irregular hexagonal prism under a pyramid tip. */
function crystalGeometry() {
  const sides = 6, ring = (y: number, r: number) => Array.from({ length: sides }, (_, i) => {
    const a = (i / sides) * Math.PI * 2, j = 1 + Math.sin(i * 2.7) * 0.12;
    return new THREE.Vector3(Math.cos(a) * r * j, y, Math.sin(a) * r * j);
  });
  const base = ring(-0.08, 0.5), shoulder = ring(0.66, 0.42), tip = new THREE.Vector3(0.04, 1, -0.02);
  const pos: number[] = [];
  const tri = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3) => pos.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
  for (let i = 0; i < sides; i++) {
    const n = (i + 1) % sides;
    tri(base[i], shoulder[n], shoulder[i]); tri(base[i], base[n], shoulder[n]);
    tri(shoulder[i], shoulder[n], tip);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();
  return g;
}

export interface CrystalSpec {
  x: number; z: number; width: number; height: number;
  /** Lean as an xz offset of the tip per unit height. */
  leanX?: number; leanZ?: number;
  life: number; delay?: number;
  /** 0 = clear blue, 1 = pale frosted. */
  frost?: number;
}

/**
 * Real ice crystals for the frost element: faceted, lit, erupting from the ground with an
 * overshoot, then settling and sinking away. One instanced draw; dead slots collapse in the
 * vertex shader, so a spawn uploads only the slot it writes.
 */
export class IceCrystals {
  readonly mesh: THREE.Mesh;
  private readonly shape: THREE.InstancedBufferAttribute;
  private readonly pose: THREE.InstancedBufferAttribute;
  private readonly time: THREE.InstancedBufferAttribute;
  private readonly dirty = new DirtyRange();
  private readonly ends: Float32Array;
  private cursor = 0;
  private used = 0;

  constructor(scene: THREE.Scene, readonly capacity = 160) {
    const base = crystalGeometry(), g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', base.getAttribute('position'));
    g.setAttribute('normal', base.getAttribute('normal'));
    const attr = () => new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(THREE.DynamicDrawUsage);
    this.shape = attr(); this.pose = attr(); this.time = attr();
    g.setAttribute('iShape', this.shape); g.setAttribute('iPose', this.pose); g.setAttribute('iTime', this.time);
    g.instanceCount = 0;
    this.ends = new Float32Array(capacity);
    this.mesh = new THREE.Mesh(g, new THREE.ShaderMaterial({
      uniforms: { uTime: fxUniforms.uTime, uFogDensity: fxUniforms.uFogDensity },
      vertexShader: /* glsl */ `
        attribute vec4 iShape, iPose, iTime;
        uniform float uTime;
        varying vec3 vN, vView; varying float vH, vSeed, vFrost, vMelt;
        ${FOG_VERT}
        void main() {
          float t = uTime - iTime.x - iTime.z, life = iTime.y;
          if (t < 0.0 || t > life || iShape.z <= 0.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
          // erupt with an overshoot, hold, then settle and sink over the last third of a second
          float u = clamp(t / 0.13, 0.0, 1.0), r1 = u - 1.0;
          float rise = 1.0 + 2.6 * r1 * r1 * r1 + 1.6 * r1 * r1;
          float melt = smoothstep(life - 0.32, life, t);
          vec3 p = position;
          p.xz *= iShape.z * (1.0 - melt * 0.35);
          p.y *= iShape.w * rise;
          p.xz += iPose.xy * p.y;
          p.y -= melt * iShape.w * 0.75;
          float c = cos(iPose.z), s = sin(iPose.z);
          p.xz = mat2(c, -s, s, c) * p.xz;
          vec3 n = normal; n.xz = mat2(c, -s, s, c) * n.xz;
          vec4 world = modelMatrix * vec4(p + vec3(iShape.x, 0.0, iShape.y), 1.0);
          vec4 mv = viewMatrix * world;
          vN = normalize(n); vView = normalize(cameraPosition - world.xyz);
          vH = position.y; vSeed = iPose.z; vFrost = iPose.w; vMelt = melt;
          FOG_FROM_MV(mv)
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        varying vec3 vN, vView; varying float vH, vSeed, vFrost, vMelt;
        ${FOG_FRAG}
        void main() {
          vec3 N = normalize(vN), V = normalize(vView), L = normalize(vec3(-0.4, 0.85, 0.45));
          float facet = fract(sin(dot(floor(N * 4.0 + 0.5), vec3(12.9, 78.2, 37.7)) + vSeed) * 43758.5);
          float ndl = max(dot(N, L), 0.0), fres = pow(1.0 - max(dot(N, V), 0.0), 3.0);
          // thick ice is deep blue at the root and pales toward the frosted tip
          vec3 deep = vec3(0.02, 0.1, 0.26), pale = vec3(0.45, 0.72, 0.95);
          vec3 col = mix(deep, pale, smoothstep(0.45, 1.0, vH) * 0.6 + vFrost * 0.25) * (0.4 + 0.6 * facet);
          col *= 0.3 + 0.9 * ndl;
          // light caught inside the crystal, a cold silhouette rim and a sharp glint on the facets
          col += vec3(0.08, 0.32, 0.75) * (0.12 + 0.22 * facet) * (1.0 - vH * 0.6);
          col += vec3(0.4, 0.75, 1.15) * fres * 0.65;
          col += vec3(0.9, 0.97, 1.0) * pow(max(dot(reflect(-L, N), V), 0.0), 48.0) * 1.2;
          // the melt whitens it into frost just before it sinks away
          col = mix(col, vec3(0.8, 0.92, 1.0), vMelt * 0.5);
          gl_FragColor = vec4(col * fogKeep(), 1.0);
        }`,
    }));
    this.mesh.name = 'iceCrystals'; this.mesh.frustumCulled = false; this.mesh.visible = false; this.mesh.renderOrder = 2;
    scene.add(this.mesh);
  }

  add(c: CrystalSpec) {
    const now = fxUniforms.uTime.value, i = this.cursor;
    this.cursor = (this.cursor + 1) % this.capacity;
    this.ends[i] = now + (c.delay ?? 0) + c.life;
    this.shape.setXYZW(i, c.x, c.z, c.width, c.height);
    this.pose.setXYZW(i, c.leanX ?? 0, c.leanZ ?? 0, Math.random() * Math.PI * 2, c.frost ?? 0);
    this.time.setXYZW(i, now, c.life, c.delay ?? 0, 0);
    this.dirty.mark(i);
    this.used = Math.max(this.used, i + 1);
  }

  /** Upload written slots and hide the draw when nothing is alive. */
  flush() {
    const now = fxUniforms.uTime.value;
    let alive = false;
    for (let i = 0; i < this.used; i++) if (this.ends[i] > now) { alive = true; break; }
    (this.mesh.geometry as THREE.InstancedBufferGeometry).instanceCount = alive ? this.used : 0;
    this.mesh.visible = alive;
    this.dirty.flush([this.shape, this.pose, this.time]);
  }
}
